import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MediaService } from '@core/services/media.service';
import { TranslateService } from '@ngx-translate/core';
import {
    inferDateFormat
} from '@salesflare/date-format-infer';

import { ImportStateModel as ImportState, MappingObservation, MappingObject } from '@shared/models/import.model';
import { ApiResponseModel as ApiResponse } from '@shared/models/services/api.response.model';

import { isUndefined } from 'lodash';

@Component({
    selector: 'sfx-import-map',
    templateUrl: './import-map.component.html',
    styleUrls: ['./import-map.component.scss']
})
export class ImportMapComponent implements OnInit, OnDestroy {

    public disableImportButton = false;
    public needsToSelectDateFormat = false;
    public disabledSelect = true;
    public isAiLoading = true;

    private _minDelayHandle: any = null;
    public enableCreateAccountCheckbox = false;
    public enableFindEmailCheckbox = false;
    public showCreateAccountCheckbox = false;
    public showFindEmailCheckbox = false;

    public createAccountIfNotExists = false;
    public updateAccountIfExists = false;
    public findEmailIfNotExists = false;
    public isFindEmailEnabled = false;
    public isRestricted = false;

    public importState: ImportState

    importId: string;

    public headers: any[] = [];
    public secondaryHeaders: any[] = [];
    public importSample: any[] = [];
    public sfHeader: any[] = [];
    public chosenHeaders: any[] = [];

    public datasource: any[] = [];
    public displayedColumns: string[] = ['file'];
    public dateFields: { [key: string]: MappingObservation } = {};

    public get hasDateFormatSelects(): boolean {
        for (const idx in this.chosenHeaders) {
            if (this.dateFields[this.headers[idx]] && Number(this.dateFields[this.headers[idx]].formats?.length) > 1 && (this.chosenHeaders[idx].isDate === true || this.chosenHeaders[idx].type?.type === 'date')) {
                return true;
            }
        }

        return false;
    }

    constructor(
        public media: MediaService,
        @Inject('importService') public importService: any,
        @Inject('importProgress') public importProgress: any,
        @Inject('modelService') public modelService: any,
        @Inject('stateService') public stateService: any,
        @Inject('stateParams') public stateParams: any,
        @Inject('flagsService') public flagsService: any,
        @Inject('rootScope') private rootScope: any,
        @Inject('$timeout') private $timeout: any,
        private translate: TranslateService
    ) {
        this.importId = this.stateParams.id;

        this.isRestricted = this.modelService.me.restricted;
        this.isFindEmailEnabled = this.flagsService.get('findEmail') === true;

    }

    public ngOnInit(): void {

        this.disableImportButton = true;

        this._loadImportData().catch(() => {});
    }

    private _loadImportData(): Promise<any> {

        // Get import
        return this.importService.getImport(this.importId).then((response: ApiResponse<ImportState>) => {

            this.importState = response.data;

            if (this.importState.type === 'contact') {
                this.showCreateAccountCheckbox = true;
                this.showFindEmailCheckbox = true;
            }

            if (this.importState.status === 'waiting' || this.importState.status === 'mapping') {

                if (this.importState.status === 'mapping') {
                    this.isAiLoading = false;
                }

                const minDelay = this.importState.status === 'waiting'
                    ? new Promise<void>((resolve) => {
                        this._minDelayHandle = this.$timeout(resolve, 2000);
                    })
                    : Promise.resolve();

                const sampleLoad = this.importService.getImportSample(this.importId, true, this.importState.type).then((sampleResponse: ApiResponse<any>) => {

                    if (sampleResponse.data.length > 0) {
                        this.headers = sampleResponse.data[0];

                        this.displayedColumns = this.displayedColumns.concat(...this.headers);

                        this.importSample = [];

                        for (let i = 1; i < sampleResponse.data.length; ++i) {
                            this.importSample.push(sampleResponse.data[i]);

                            const datasourceEntry: any = {
                                file: `Sample ${i + 1}`
                            };

                            this.headers.forEach((header, index) => {
                                datasourceEntry[header] = sampleResponse.data[i][index];
                            });

                            this.datasource.push(datasourceEntry);
                        }

                        this.secondaryHeaders = ['file_description'].concat(...this.headers.map((header) => header.concat('_description')));

                        return this.importService.getMapping(this.importId).then((mappingResponse: ApiResponse<{ [key: string]: string | MappingObject }>) => {

                            if (mappingResponse) {
                                const savedMapping = mappingResponse.data;

                                if (savedMapping) {
                                    for (const key of Object.keys(savedMapping)) {
                                        const field = savedMapping[key];

                                        // eslint-disable-next-line angular/typecheck-object
                                        if (typeof field === 'object' && field.formats) {
                                            this.dateFields[field.name] = {
                                                chosenFormat: field.chosenFormat,
                                                formats: field.formats
                                            };

                                            if (field.chosenFormat === null) {

                                                this.needsToSelectDateFormat = true;
                                            }

                                            savedMapping[key] = field.name;
                                        }
                                        // eslint-disable-next-line angular/typecheck-object
                                        else if (typeof field === 'object' && field.isNaturalLanguage === true) {
                                            this.dateFields[field.name] = {
                                                isNaturalLanguage: true
                                            };

                                            savedMapping[key] = field.name;
                                        }
                                    }
                                }

                                return this.importService.getFields(this.importState.type).then((fieldsResponse: ApiResponse<any>) => {

                                    return this.setFields(fieldsResponse.data, savedMapping);
                                });
                            }
                        });
                    }
                });

                return Promise.all([sampleLoad, minDelay])
                    .catch(() => {})
                    .finally(() => {
                        this.isAiLoading = false;
                    });
            }

            // Redirect settings/imports
            this.isAiLoading = false;
            return this.stateService.go('settings.import');
        });
    }

    public ngOnDestroy(): void {

        if (this._minDelayHandle !== null) {
            this.$timeout.cancel(this._minDelayHandle);
        }
    }

    public back(): void {

        this.rootScope.back();
    }

    private enableCheckboxes(): void {

        const accountNameField = this.chosenHeaders.find(function (field) {

            return field && field.api_field === 'account__name';
        });

        this.enableCreateAccountCheckbox = accountNameField;

        // Check if find email checkbox needs to be enabled ((First name AND Last name) OR Full name) AND (Account name OR account website)
        // Account name or account website needed
        let accountWebsiteField;
        if (!accountNameField) {
            accountWebsiteField = this.chosenHeaders.find((field) => field && field.api_field === 'account__website');
        }

        if (accountWebsiteField || accountNameField) {
            const fullNameField = this.chosenHeaders.find((field) => field && field.api_field === 'name');
            let firstNameField;
            let lastNameField;
            if (!fullNameField) {
                firstNameField = this.chosenHeaders.find((field) => field && field.api_field === 'firstname');
                lastNameField = this.chosenHeaders.find((field) => field && field.api_field === 'lastname');
            }

            this.enableFindEmailCheckbox = fullNameField || (firstNameField && lastNameField);
        }
        else {
            this.enableFindEmailCheckbox = false;
        }
    }
    public async updateMapping(idx: number): Promise<void> {
        this.enableCheckboxes();
        this.importButtonStatusCheck();

        const sfField = this.chosenHeaders[idx];
        const header = this.headers[idx];

        if (sfField.isDate || sfField.type?.type === 'date') {
            // If in the mapping you already have a date format saved then all is good, just update the field name
            if (!this.dateFields?.[header]) {
                const inferredFormat = await this.inferFormatForDateColumns(idx);

                if (inferredFormat) {
                    this.dateFields[header] = inferredFormat;
                }
            }
        }

        if (!this.disableImportButton) {
            this.saveMapping();
        }
    }

    public updateDateFormat(): void {

        let canStartTheImport = true;
        // If there are no date fields with chosenFormat null => import can happen
        for (const field of Object.keys(this.dateFields)) {
            if (this.chosenHeaders.some((header) => header.api_field === field) && this.dateFields[field].chosenFormat === null) {
                canStartTheImport = false;
            }
        }

        if (canStartTheImport) {
            this.needsToSelectDateFormat = false;
        }

        if (!this.disableImportButton) {
            this.saveMapping();
        }
    }

    public importButtonStatusCheck(): void {

        const definedHeader = this.chosenHeaders.find((header: any) => {

            return !isUndefined(header);
        });

        this.disableImportButton = !!isUndefined(definedHeader);
    }

    public saveMapping(): Promise<any> {

        const mapping: any = {};

        this.chosenHeaders.forEach((field, index) => {

            if (isUndefined(field)) {
                return;
            }

            let header = this.headers[index];

            // Header was mapped to a date field so we need to save an object with format details
            if ((field.isDate === true || field.type?.type === 'date') && this.dateFields[this.headers[index]]) {
                header = {
                    name: header
                };

                if (this.dateFields[this.headers[index]].chosenFormat || this.dateFields[this.headers[index]].formats) {
                    header.chosenFormat = this.dateFields[this.headers[index]].chosenFormat;
                    header.formats = this.dateFields[this.headers[index]].formats;
                }
                else if (this.dateFields[this.headers[index]].isNaturalLanguage === true) {
                    header.isNaturalLanguage = true;
                }
            }

            if (field.isStandardField || field.predefined_customfield) {
                mapping[field.api_field] = header;
                return;
            }

            let hierarchicalFieldName = 'custom__' + field.api_field;

            if (field.type.type.includes('select', 'autocomplete', 'multiselect', 'tags')) {
                hierarchicalFieldName += '__name';
            }

            mapping[hierarchicalFieldName] = header;
        });

        return this.importService.setMapping(this.importState.id, mapping, this.findEmailIfNotExists, this.createAccountIfNotExists, this.updateAccountIfExists)
            .catch(() => {}); // Don't throw unhandled exception
    }

    public startImport(): void {

        this.disableImportButton = true;

        this.saveMapping().then(() => {

            return this.importService.start(this.importState.id).then(() => {

                this.importProgress.startPolling();

                return this.stateService.go('settings.import.' + this.importState.type);
            });
        })
            .catch(() => {}) // Don't throw unhandled exception
            .finally(() => {

                this.disableImportButton = false;
            });
    }

    public async setFields(fields: any[], savedMapping: any): Promise<void> {

        if (fields && fields.length > 0) {
            this.sfHeader = fields;

            let enableImportButton = false;
            // Uppercase header array to do case insensitive comparison
            const upperCasedHeaders = this.headers.map((headerEntry: any) => {

                return headerEntry.toUpperCase();
            });

            // If a mapping was saved, don't try to auto match headers
            if (Object.keys(savedMapping).length > 0) {
                for (const header of Object.keys(savedMapping)) {
                    let field = this.sfHeader.find((headerField) => headerField.api_field === header);

                    if (!field) {
                        // Try removing generated hierarchicalFieldName. See saveMapping function above
                        let cleanedHeader = header.replace(/^(custom__)/, '');
                        cleanedHeader = cleanedHeader.replace(/(__name)$/, '');
                        field = this.sfHeader.find((headerField) => headerField.api_field === cleanedHeader);

                        if (!field) {
                            // The saved mapping wasn't correct, abort
                            return;
                        }
                    }

                    const indexOf = upperCasedHeaders.indexOf(savedMapping[header].toUpperCase());

                    if (indexOf > -1) {
                        enableImportButton = true;
                        this.chosenHeaders[indexOf] = field;
                    }
                }
            }
            else {
                for (const header in this.sfHeader) {
                    const indexOf = upperCasedHeaders.indexOf(this.sfHeader[header].name.toUpperCase());

                    if (indexOf > -1) {
                        enableImportButton = true;
                        this.chosenHeaders[indexOf] = this.sfHeader[header];
                    }
                }
            }

            // For backwards compatibility (working with already existing mappings) => look through the chosenHeaders and check for those that are date types. For those, try the infer and update the mapping if needed
            let shouldUpdateMapping = false;
            for (const key in this.chosenHeaders) {
                if (this.chosenHeaders[key]?.isDate || this.chosenHeaders[key]?.type?.type === 'date') {
                    if (!this.dateFields[this.headers[key]]) {
                        // eslint-disable-next-line no-await-in-loop
                        const inferredFormat = await this.inferFormatForDateColumns(+key);

                        if (inferredFormat) {
                            this.dateFields[this.headers[key]] = inferredFormat;
                            shouldUpdateMapping = true;
                        }
                    }
                }
            }

            if (shouldUpdateMapping) {
                await this.saveMapping();
            }

            this.disabledSelect = false;

            this.enableCheckboxes();

            if (enableImportButton) {
                this.disableImportButton = false;
            }
        }
    }

    public compareHeaders(option: any, selected: any): boolean {

        if (selected) {

            if (selected.id) {
                return option.id === selected.id;
            }

            return option.api_field === selected.api_field;
        }

        return false;
    }

    public getInfoboxText(): string {

        return this.translate.instant('Salesflare\'s AI has mapped your data to the most relevant {{entity}}-level fields. You can select more Salesflare fields from the dropdown menus to import those columns of data.', { entity: this.importState?.type });
    }

    public getImportTagText(): string {
        const baseText = this.translate.instant('After importing, you can filter on the following tag to see your imported data:');
        return `${baseText} <span class="accent">${this.importState?.import_tag}</span>`;
    }

    public isAmbiguousDateField(idx: number): boolean {
        return !!this.dateFields[this.headers[idx]] && Number(this.dateFields[this.headers[idx]].formats?.length) > 1 && (this.chosenHeaders[idx]?.isDate === true || this.chosenHeaders[idx]?.type?.type === 'date');
    }

    private async inferFormatForDateColumns(idx: number): Promise<MappingObservation | null> {

        const dates = this.importSample.map((row) => row[idx]);
        const inferResult = inferDateFormat(dates, { maxSamples: dates.length });

        if (inferResult.format) {
            return {
                chosenFormat: inferResult.format,
                formats: [inferResult.format]
            };
        }
        else if (inferResult.possibleFormats?.length > 1) {
            const inferredFormat: ApiResponse<{ format?: string | string[], isNaturalLanguage?: boolean }> = await this.importService.inferFormat(this.importId, this.headers[idx]);

            // eslint-disable-next-line angular/typecheck-array
            if (inferredFormat?.data?.format && Array.isArray(inferredFormat.data.format) && inferredFormat.data.format.length > 0) {

                this.needsToSelectDateFormat = true;
                return {
                    chosenFormat: null,
                    formats: inferredFormat.data.format
                };
            }
            // eslint-disable-next-line angular/typecheck-string
            else if (inferredFormat?.data?.format && typeof inferredFormat.data.format === 'string') {
                return {
                    chosenFormat: inferredFormat.data.format,
                    formats: [inferredFormat.data.format]
                };
            }
            else if (inferredFormat.data.isNaturalLanguage === true) {
                return {
                    isNaturalLanguage: true
                };
            }
        }
        else if (inferResult.isNaturalLanguage === true) {
            return {
                isNaturalLanguage: true
            };
        }

        return null;
    }
}
